/* eslint-disable max-len */
import { useMemo, useEffect, Fragment, useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useCookies } from 'react-cookie';
import { getTeamData, updateTeamMemberRole, deleteTeamMember } from 'store/actions/teamData';
import styled, { withTheme } from 'styled-components';
import {
    FovLoader,
    FovOption,
    FovSelect,
    DeleteOutlined,
    PlusOutlined,
    HorizontalSpacer,
    VerticalSpacer,
    WarningOutlined,
    FovConfirmation,
    Body,
} from 'components';
import { FovButton } from 'components/generic';
import { getColor } from 'utils';
import { AddMemberForm } from './AddMemberForm';

const teamRoles = [ 'admin', 'member' ];


const MemberSection = styled('div')`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.5rem;
    background-color: ${props => !props.isOdd ? getColor(props, 'bgCellOdd') : getColor(props, 'bgCell')};
    height: 100%;

    border-bottom: 0.0625rem solid #eee;
    border-color: ${props => getColor(props, 'mediumGrey')};
    &:last-child: {
        border-bottom: none;
    }
`;

const HeaderSection = styled(MemberSection)`
    background-color: ${props => getColor(props, 'mediumGrey')};
`;

const MemberContainer = styled('div')`
    border: 0.0625rem solid #ccc;
    border-radius: 0.25rem;
    border-color: ${props => getColor(props, 'mediumGrey')};
`;

const NameContainer = styled('div')`
    padding-right: 0.5rem;
    min-width: 15rem;
`;

const RoleContainer = styled('div')`
    position: relative;
    width: 15rem;
    padding: 0 1rem;
    margin-right: 5rem;
    margin-left: auto;
`;

const IconButton = styled(FovButton)`
    width: fit-content;
    padding: 0;
    background-color: ${props => getColor(props, 'error')};

    &:hover {
       color: ${props => getColor(props, 'errorHover')};
       background-color: ${props => getColor(props, 'primary')};
    }
`;

const AddMemberButton = styled(FovButton)`
    position: relative;
    width: fit-content;
    cursor: pointer;
    background-color: ${props => getColor(props, 'accent')};
    margin-right: 0;
    margin-left: auto;
    color: ${props => getColor(props, 'onPrimary')};

    &:hover {
        background-color: ${props => getColor(props, 'accentHover')};
    }
`;

const Members = withTheme(props => {
    const { theme } = props;
    const dispatch = useDispatch();

    const teamDataStore = useSelector(state => state.teamData);
    const userDataStore = useSelector(state => state.userData);

    const [ cookies ] = useCookies([ 'userAuth' ]);
    const token = cookies.archvision_token;

    const selectedTheme = userDataStore.userPreferences.theme;
    const errorColor = theme.colors[selectedTheme].error;
    const errorHoverColor = theme.colors[selectedTheme].errorHover;
    const users = useMemo(() => teamDataStore.users, [ teamDataStore.users ]);
    const teamId = useMemo(() => teamDataStore.teamId, [ teamDataStore.teamId ]);
    const allowedDomains = useMemo(() => teamDataStore.allowedDomains, [ teamDataStore.allowedDomains ]);
    const selectedTeamId = useMemo(() => teamDataStore.selectedTeamId, [ teamDataStore.selectedTeamId ]);
    const teamDataIsLoading = useMemo(() => teamDataStore.isLoading, [ teamDataStore.isLoading ]);
    const loadingUserId = useMemo(() => teamDataStore.loadingUserId, [ teamDataStore.loadingUserId ]);
    const [ userToBeDeleted, setUserToBeDeleted ] = useState(null);
    const [ showMemberDelete, setShowMemberDelete ] = useState(false);
    const [ showAddMemberForm, setShowAddMemberForm ] = useState(false);
    const [ isDeleting, setIsDeleting ] = useState(false);

    const sortedUsers = users?.toSorted((u1,u2) => {
        const [ f1, l1 ] = [ u1.firstName, u1.lastName ];
        const [ f2, l2 ] = [ u2.firstName, u2.lastName ];
        if (l1.localeCompare(l2) === 0) return f1.localeCompare(f2);
        return l1.localeCompare(l2);
    });

    useEffect(() => {
        if (selectedTeamId !== teamId) {
            dispatch(getTeamData(token, selectedTeamId));
        }
    }, [ teamId, selectedTeamId ]);

    const handleMemberRoleUpdate = useCallback((userId, role) => {
        dispatch(updateTeamMemberRole(token, teamId, userId, role));
    }, [ teamId ]);

    const handleDeleteMember = useCallback(userId => {
        const onComplete = () => {
            setIsDeleting(false);
            setUserToBeDeleted(null);
            setShowMemberDelete(false);
        };
        setIsDeleting(true);
        dispatch(deleteTeamMember(token, teamId, userId, onComplete));
    }, [ teamId, setUserToBeDeleted, setShowMemberDelete ]);

    return (
        <FovLoader
            text='Retrieving team data'
            resource={!teamDataIsLoading}
        >
        {users?.length === 0 && <div>No users</div>}
        {sortedUsers?.length > 0 &&
            <Fragment>
                <AddMemberButton
                    $selectedTheme={selectedTheme}
                    onClick={() => setShowAddMemberForm(true)}
                >
                    <>Add Member</>
                    <HorizontalSpacer size={6} />
                    <PlusOutlined width={20}/>
                </AddMemberButton>
                <VerticalSpacer size={5} />
                <MemberContainer $selectedTheme={selectedTheme} >
                    <HeaderSection $selectedTheme={selectedTheme}>
                        <NameContainer>Name (Last, First)   </NameContainer>
                    </HeaderSection>

                    {sortedUsers?.map((user, index) => (
                        <MemberSection $selectedTheme={selectedTheme} key={index} isOdd={index % 2 !== 0}>
                            <NameContainer>
                                {user.lastName}, {user.firstName} ({user.email})
                            </NameContainer>
                            {!allowedDomains.includes('@' + user.email.split('@')[1]) &&
                                <WarningOutlined
                                    title="Member does not have an allowed domain."
                                />
                            }
                            <FovLoader text='Removing...' resource={user.userId !== userToBeDeleted?.userId}>
                            <FovLoader text='Updating role...' resource={user.userId !== loadingUserId}>
                            <RoleContainer>
                                <FovSelect
                                    value={user.role}
                                    placeholder={'Select a role.'}
                                    onChange={(e,newVal) => {
                                        if (user.role !== newVal) {
                                            handleMemberRoleUpdate(user.userId, newVal);
                                        }
                                    }}
                                >
                                    {teamRoles.map(role => (
                                        <FovOption key={role} value={role}>
                                            {role}
                                        </FovOption>
                                    ))}
                                </FovSelect>
                            </RoleContainer>
                            </FovLoader>
                            <IconButton
                                theme={theme}
                                $selectedTheme={selectedTheme}
                                onClick={() => {
                                    setUserToBeDeleted(user);
                                    setShowMemberDelete(true);
                                }}
                                icon={
                                    <DeleteOutlined
                                        color={theme.colors[selectedTheme].white}
                                        hoverColor={theme.colors[selectedTheme].errorHover}
                                    />
                                }
                            />
                            </FovLoader>
                        </MemberSection>
                    ))}
                </MemberContainer>
                <FovConfirmation
                    onConfirm={() => handleDeleteMember(userToBeDeleted.userId)}
                    onClose={() => {
                        setUserToBeDeleted(null);
                        setShowMemberDelete(false);
                    }}
                    ignoreConfirmClose={true}
                    confirmationText={!isDeleting ? 'Remove member' : 'Removing...'}
                    isOpened={showMemberDelete}
                    confirmColor={errorColor}
                    confirmHoverColor={errorHoverColor}
                    disabled={isDeleting}
                >
                    <Body size='Medium'>
                        {`Are you sure you want to remove ${userToBeDeleted?.email || 'selected member'} from the team?`}
                    </Body>
                </FovConfirmation>
                <AddMemberForm
                    isVisible={showAddMemberForm}
                    onClose={() => setShowAddMemberForm(false)}
                />
                </Fragment>
        }
        </FovLoader>
    );
});

export { Members };
